import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import useSiteState from '@app/state/SiteState';
import ModalHandler from '@app/component/overlays/modal/ModalHandler';
import { useCompanyHotels } from '@app/site/secured/rateshopper/rates/provider/useCompanyHotels';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import useCompanyHotelForm
	from '@app/site/secured/rateshopper/rates/settings/page/competitors/CompanyHotelFormValidation';
import useAppForm from '@app/site/_form';
import CompanyHotelInput from '@app/model/company_hotel/input/CompanyHotelInput';
import SearchHotelResult from '@app/component/form/input/autocomplete/SearchHotelResult';
import { useCreateOrSaveCompanyHotelMutation } from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import SetupPropertyHotelCompetitorSuggestionsModal
	from '@app/site/registration/overlay/SetupPropertyHotelCompetitorSuggestionsModal';
import Message from '@app/component/message/Message';
import { HotelField } from '@app/site/_form/field/HotelField';
import FormButton from '@app/component/form/button/FormButton';
import { CheckCircleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { FieldMessages } from '@app/site/_form/field/FieldMessages';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { ResetButton } from '@app/site/_form/button/ResetButton';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { CompanyHotelQueryKeys } from '@app/model/company_hotel/service/CompanyHotelQueryKeys';
import Spinner from '@app/component/static/Spinner';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import CompanyHotelMutationResult from '@app/model/company_hotel/CompanyHotelMutationResult';
import ProcessRequestQueryKeys from '@app/model/process-request/service/ProcessRequestQueryKeys';
import ProcessRequestService from '@app/model/process-request/service/ProcessRequestService';

const CompanyHotelForm: React.FunctionComponent<{
	companyHotel?: CompanyHotel,
	hasAlerts?: boolean
}> = ({ companyHotel, hasAlerts }) => {

	const suggestionsModal = useRef<ModalHandler>(null);
	const principal = useSiteState(state => state.principal)!;
	const { companyHotels } = useCompanyHotels();
	const toaster = useToaster();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const {
		companyHotelFormValidationSchema,
		companyHotelFormDefaultValues,
	} = useCompanyHotelForm(principal, companyHotels, companyHotel);

	const maxNumberOfCompetitors = !!companyHotel ? companyHotel.settings.main.maxNumberOfCompetitors : 12;

	const form = useAppForm({
		defaultValues: companyHotelFormDefaultValues() as CompanyHotelInput,
		validators: {
			onChange: companyHotelFormValidationSchema(maxNumberOfCompetitors),
		},
		onSubmit: ({ value }) => savePropertyMutation.mutateAsync(value).catch(() => {
		}),

	});

	const openSuggestions = (hotelId: number) => {
		if (hotelId > 0) {
			suggestionsModal.current?.open(hotelId);
		}
	};

	const onAddCompetitors = (competitors: SearchHotelResult[]) => {
		const competitorHotelIdsFieldName = 'competitorHotelIds';
		const existingIds = form.getFieldValue(competitorHotelIdsFieldName)?.filter(id => id > 0) || [];

		const newIds = competitors.map(c => c.id);
		const allIds = [...new Set([...existingIds, ...newIds])];
		form.setFieldValue(competitorHotelIdsFieldName, allIds);
		form.validateField(competitorHotelIdsFieldName, 'change');
	};

	const removeCompetitor = (index: number) => {
		const competitorHotelIdsFieldName = 'competitorHotelIds';
		const currentIds = form.getFieldValue(competitorHotelIdsFieldName) ?? [];
		form.setFieldValue(competitorHotelIdsFieldName, currentIds.filter((_, i) => i !== index));
		form.validateField(competitorHotelIdsFieldName, 'change');
	};

	const queryClient = useQueryClient();
	const [pendingSaveResult, setPendingSaveResult] = useState<CompanyHotelMutationResult | null>(null);
	const [completedRanking, setCompletedRanking] = useState<{
		result: CompanyHotelMutationResult,
		rankingFailed: boolean,
	} | null>(null);

	const rankingQueries = useQueries({
		queries: (pendingSaveResult?.rankingExecutions ?? []).map(execution => ({
			queryKey: ProcessRequestQueryKeys.executionState(execution.requestId),
			queryFn: () => ProcessRequestService.findProcessRequestExecutionState(execution.requestId),
			initialData: execution,
			refetchInterval: 1000,
			refetchOnWindowFocus: true,
		})),
	});

	const finishSave = useCallback(async (savedCompanyHotel: CompanyHotel, rankingFailed = false) => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: CompanyHotelQueryKeys.detail(savedCompanyHotel.id),
			}),
			queryClient.invalidateQueries({
				queryKey: CompanyHotelQueryKeys.list(),
			}),
		]);

		if (rankingFailed) {
			toaster.error(
				`${companyHotel ? 'Save' : 'Create'} a property`,
				'Property saved, but one or more competitors could not be ranked',
			);
		} else {
			toaster.success(`${companyHotel ? 'Save' : 'Create'} a property`, 'Property saved successfully');
		}

		if (!companyHotel) {
			navigate('/secured/dashboard');
		}
	}, [companyHotel, navigate, queryClient, toaster]);

	const savePropertyMutation = useCreateOrSaveCompanyHotelMutation(
		{
			isNew: !companyHotel,
			onSuccess: (result) => {
				if (result.rankingExecutions.length === 0) {
					void finishSave(result.companyHotel);
					return;
				}
				setPendingSaveResult(result);
			},
			onError: (error) => toaster.error(`${companyHotel ? 'Save' : 'Create'} a property`, t(error)),
		},
	);

	useEffect(() => {
		if (!pendingSaveResult) return;

		if (rankingQueries.some(query => query.isError)) {
			const savedCompanyHotel = pendingSaveResult.companyHotel;
			setPendingSaveResult(null);
			void finishSave(savedCompanyHotel, true);
			return;
		}

		if (rankingQueries.length !== pendingSaveResult.rankingExecutions.length ||
			rankingQueries.some(query => query.data?.isFinished !== true)) {
			return;
		}

		const rankingFailed = rankingQueries.some(query => query.data?.isSuccessful !== true);
		setPendingSaveResult(null);
		setCompletedRanking({ result: pendingSaveResult, rankingFailed });
	}, [finishSave, pendingSaveResult, rankingQueries]);

	const closeCompletedRanking = useCallback(() => {
		if (!completedRanking) return;

		const { result, rankingFailed } = completedRanking;
		setCompletedRanking(null);
		void finishSave(result.companyHotel, rankingFailed);
	}, [completedRanking, finishSave]);

	const isSaveModalOpen = savePropertyMutation.isPending || pendingSaveResult !== null || completedRanking !== null;
	const finishedRankingCount = rankingQueries.filter(query => query.data?.isFinished).length;
	const totalRankingCount = pendingSaveResult?.rankingExecutions.length ?? 0;

	return <main>
		<SetupPropertyHotelCompetitorSuggestionsModal ref={suggestionsModal} onSave={onAddCompetitors} />
		<Transition show={isSaveModalOpen} as={Fragment}>
			<Dialog as="div" className="relative z-[100]" onClose={() => {}}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-white-50 bg-opacity-75 transition-opacity backdrop-blur-sm backdrop-filter" />
				</TransitionChild>

				<div className="fixed inset-0 z-[100] overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-8 py-7 text-center shadow-2xl transition-all sm:w-full sm:max-w-sm">
								<div className="flex flex-col items-center gap-y-4">
									{completedRanking
										? <CheckCircleIcon className="h-12 w-12 text-green-500" aria-hidden="true" />
										: <Spinner className="h-10 w-10 animate-spin fill-blue-100 text-blue-600" />}
									<Dialog.Title as="h4" className="text-base font-normal leading-6 text-gray-900">
										{completedRanking
											? 'Competitor ranking finished'
											: pendingSaveResult
											? `Ranking competitors (${finishedRankingCount}/${totalRankingCount})`
											: 'Saving property'}
									</Dialog.Title>
									{completedRanking && <>
										<p className="text-sm leading-5 text-gray-500">
											{completedRanking.rankingFailed
												? 'The property was saved, but one or more competitors could not be ranked.'
												: 'The property was saved and all competitors have been ranked.'}
										</p>
										<FormButton state="primary" onClick={closeCompletedRanking} extraClassName="mt-2 w-full">
											Close
										</FormButton>
									</>}
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>

		<form.AppForm>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}>
				<div
					className="mx-auto lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
					<div>
						<h2 className="text-base font-semibold leading-5 text-gray-900">
							{!companyHotel ? 'Add New Property' : 'Edit your Property'}
						</h2>
						<p className="text-sm leading-6 text-gray-500">
							Manage your property below. Add or remove your competitors <span
							className="text-xs font-light leading-6 text-gray-400 italic">(up to {maxNumberOfCompetitors})</span> as
							desired.
						</p>
					</div>
					<div className="mt-4 pt-8 border-t border-gray-200">
						{hasAlerts &&
							<Message state="warning" extraClassName="mb-5">There are alerts configured for this property. Altering the
								property may remove or alter the alerts associated with it!</Message>}
					</div>

					<form.AppField name="hotelId">
						{(hotelIdField) => {
							return <>
								<div className="bg-gray-50 px-4 py-5 sm:px-8 shadow-sm ring-1 ring-gray-900/10 rounded-xl">
									<div>
										<div className="relative flex items-center justify-between"><span
											className="pr-3 text-sm font-semibold leading-6 text-gray-800">Your Hotel</span>
										</div>
										<div className="mt-0.5">
											<HotelField placeholder="Insert property name" disabled={!!companyHotel} />
										</div>
										<div className="mt-4">
											{principal!.isFromWebhotelier && (
												<form.AppField name="webhotelierId">
													{(field) => (
														<field.TextField
															label="Webhotelier ID"
															extraClassName="-ml-px rounded-l-none"
														/>
													)}
												</form.AppField>
											)}
										</div>
										{hotelIdField.state.value > 0 && (
											<form.AppField name="competitorHotelIds" mode="array">
												{(competitorHotelIds) => (
													<div className="mt-10">
														<div className="relative">
															<div className="absolute inset-0 flex items-center"
																	 aria-hidden="true">
																<div className="w-full border-t border-gray-300"></div>
															</div>
															<div className="relative flex items-center justify-between">
															<span
																className="bg-gray-50 pr-3 text-base font-semibold leading-6 text-gray-900">
																Competitors
															</span>
																<button
																	type="button"
																	// @ts-ignore
																	onClick={() => openSuggestions(hotelIdField.state.value)}
																	className="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
																>
																	<PlusIcon
																		className="-ml-1 -mr-0.5 h-5 w-5 text-gray-400" />
																	<span>See Suggestions</span>
																</button>
															</div>
														</div>
														<div className="mt-4">
															<ul role="list" className="divide-y divide-gray-200">
																{competitorHotelIds.state.value.map((id, j) => {
																	return (
																		<form.AppField
																			name={`competitorHotelIds[${j}]`}
																			key={id > 0 ? id : `new-${j}`}
																		>
																			{(_) => (
																				<li className="py-4">
																					<div className="flex space-x-4">
																						<HotelField placeholder="Insert competitor"
																												disabled={!!companyHotel && companyHotel.hasHotelCompetitorWithHotelId(id)} />
																						<div>
																							<FormButton
																								state="white"
																								onClick={() => removeCompetitor(j)}
																								extraClassName="px-3"
																							>
																								<XMarkIcon
																									className="w-5 h-5 text-rose-400" />
																							</FormButton>
																						</div>
																					</div>
																				</li>
																			)}
																		</form.AppField>
																	);
																})}
															</ul>
															<div>
																<FieldMessages meta={competitorHotelIds.state.meta} />
															</div>
														</div>
														<div className="flex mt-2 pt-2 border-t border-gray-200">
															<form.Subscribe
																selector={(state) => state.values.competitorHotelIds ?? []}
															>
																{(competitorIds) => (
																	<>
																		{competitorIds.length < maxNumberOfCompetitors &&
																			competitorIds.findIndex((v) => v === 0) === -1 && (
																				<FormButton
																					state="text"
																					onClick={() => competitorHotelIds.pushValue(0)}
																					className="text-sm font-semibold leading-6 text-blue-600 hover:text-blue-500"
																				>
																					<span aria-hidden="true" className="mr-1">+</span> Add another competitor
																				</FormButton>
																			)}
																	</>
																)}
															</form.Subscribe>
														</div>
													</div>
												)}
											</form.AppField>
										)}
									</div>
								</div>
								<div className="flex items-center justify-end gap-x-2 mt-4">
									<ResetButton state="text">Reset</ResetButton>
									<SubmitButton state="primary">Save</SubmitButton>
								</div>
							</>;
						}}
					</form.AppField>
				</div>

			</form>
		</form.AppForm>
	</main>;

};

export default CompanyHotelForm;
